const API_BASE = import.meta.env.VITE_API_URL || '/api';
const REFRESH_PATH = '/auth/refresh';
const AUTH_RETRY_EXCLUDE = new Set([REFRESH_PATH, '/auth/login', '/auth/register', '/auth/logout']);

let refreshInFlight: Promise<boolean> | null = null;

function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf-token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function buildHeaders(options: RequestInit): Record<string, string> {
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  // Attach CSRF token for state-changing requests
  const csrf = getCsrfToken();
  if (csrf) {
    headers['X-CSRF-Token'] = csrf;
  }

  return headers;
}

async function parseResponseBody(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const res = await fetch(`${API_BASE}${REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

export async function apiFetch<T = any>(path: string, options: RequestInit = {}, allowRetry = true): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: buildHeaders(options),
    credentials: 'include',
  });

  if (res.status === 401 && allowRetry && !AUTH_RETRY_EXCLUDE.has(path)) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return apiFetch<T>(path, options, false);
    }
    // Refresh failed — session is gone. Hard redirect to login.
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (res.status === 401) {
    // Don't hard-redirect here — callers with allowRetry=false (e.g. AuthProvider's
    // initial /auth/me check) handle 401 gracefully. React Router's ProtectedRoute
    // will redirect to /login when user is null. A window.location.href here would
    // cause an infinite full-page-reload loop.
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await parseResponseBody(res);
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  if (res.status === 204) {
    return {} as T;
  }

  return parseResponseBody(res);
}
